import { InvitationCollectionActionTaken } from '../enums/invitation-collection.enum'
import { InvitationChannelHistoryState, InvitationCollectionItem } from '../types/invitation-tracker.type'
import {
  getActiveInvitationChannelHistory,
  getInvitationChannelLatestStatus,
  InvitationChannel,
  invitationHistoryStatusTone,
  invitationStatusLookupKey
} from './invitation-channel-status.util'

/** Why the channel history footer replaces the Send CTA. */
export type InvitationChannelBlockedSendReason =
  | 'event_ended'
  | 'registered'
  | 'declined'
  | 'unreachable'
  | 'opt_out'
  | 'channel_failed'
  | 'channel_bounced'

export type InvitationChannelBlockedSendState = {
  reason: InvitationChannelBlockedSendReason | null
  showSendCta: boolean
  message: string
}

/**
 * Action-taken statuses that hide the channel Send CTA on every channel.
 */
const BLOCKED_SEND_ACTION_TAKEN = new Set<InvitationCollectionActionTaken>([
  InvitationCollectionActionTaken.REGISTERED,
  InvitationCollectionActionTaken.DECLINED,
  InvitationCollectionActionTaken.UNREACHABLE
])

/**
 * Channels where the collection `is_opt_out` flag blocks Send.
 */
const EMAIL_ONLY_BLOCKING_CHANNELS = new Set<InvitationChannel>(['email'])

/**
 * Red channel statuses that never resolve to `channel_failed`:
 * bounced has its own reason, opt-out is gated by collection `is_opt_out`, and payment failed stays display-only so the invitation can be resent.
 */
const NON_FAILED_RED_STATUS_KEYS: ReadonlySet<string> = new Set(['bounced', 'opt out', 'payment failed'])

/**
 * Returns true when the latest channel invite status is bounced (invitation status, not action taken).
 * @param status {string | null | undefined} - Latest invite status for the channel.
 */
export function isInvitationChannelBouncedStatus(status: string | null | undefined): boolean {
  return invitationStatusLookupKey(status) === 'bounced'
}

/**
 * Returns true when the latest channel invite status is a red resolution for that channel
 * (email / WhatsApp: Failed / Complaint / Error; SMS: Failed / Not supported).
 * Statuses in {@link NON_FAILED_RED_STATUS_KEYS} are excluded.
 * @param status {string | null | undefined} - Latest invite status for the channel.
 * @param channel {InvitationChannel} - Channel the status belongs to.
 */
export function isInvitationChannelFailedStatus(status: string | null | undefined, channel: InvitationChannel): boolean {
  if (!status || NON_FAILED_RED_STATUS_KEYS.has(invitationStatusLookupKey(status))) return false
  return invitationHistoryStatusTone(status, channel) === 'failed'
}

/**
 * Resolves why Send is blocked for a channel panel.
 * Registered / Declined / Unreachable (action taken) win over channel invitation failure.
 * Bounced / failed only block that channel so another channel can still be used.
 * @param actionTaken {InvitationCollectionActionTaken | null | undefined} - Collection action taken.
 * @param latestChannelStatus {string | null | undefined} - Latest invitation status on the open channel.
 * @param channel {InvitationChannel} - Open channel (for invitation-status mapping).
 */
export function getInvitationChannelBlockedSendReason(
  actionTaken: InvitationCollectionActionTaken | null | undefined,
  latestChannelStatus: string | null | undefined,
  channel: InvitationChannel
): InvitationChannelBlockedSendReason | null {
  if (actionTaken === InvitationCollectionActionTaken.REGISTERED) return 'registered'
  if (actionTaken === InvitationCollectionActionTaken.DECLINED) return 'declined'
  if (actionTaken === InvitationCollectionActionTaken.UNREACHABLE) return 'unreachable'
  if (isInvitationChannelBouncedStatus(latestChannelStatus)) return 'channel_bounced'
  if (isInvitationChannelFailedStatus(latestChannelStatus, channel)) return 'channel_failed'
  return null
}

/**
 * Returns true when Send remains available for this action-taken + channel invitation status pair.
 * @param actionTaken {InvitationCollectionActionTaken | null | undefined} - Collection action taken.
 * @param latestChannelStatus {string | null | undefined} - Latest invitation status on the open channel.
 * @param channel {InvitationChannel} - Open channel.
 */
export function canSendInvitationOnChannel(
  actionTaken: InvitationCollectionActionTaken | null | undefined,
  latestChannelStatus: string | null | undefined,
  channel: InvitationChannel
): boolean {
  return getInvitationChannelBlockedSendReason(actionTaken, latestChannelStatus, channel) == null
}

/**
 * Builds the blocked-send info banner copy for event ended / Registered / Declined / Unreachable / Opt out / failed / bounced channel footers.
 * Returns an empty string when send is still allowed.
 * @param reason {InvitationChannelBlockedSendReason | null | undefined} - Resolved block reason.
 * @param inviteeName {string | null | undefined} - Invitee display name.
 */
export function invitationChannelBlockedSendMessage(reason: InvitationChannelBlockedSendReason | null | undefined, inviteeName?: string | null): string {
  if (!reason) return ''

  const name = inviteeName?.trim() || 'This invitee'

  switch (reason) {
    case 'event_ended':
      return 'This event has ended. No further invitations can be sent.'
    case 'registered':
      return `${name} is already registered, no new invitation can be sent.`
    case 'declined':
      return `${name} declined the invitation, no new invitation can be sent.`
    case 'unreachable':
      return `${name} is unreachable, no new invitation can be sent.`
    case 'opt_out':
      return `${name} has opted out, try another channel to resend the invitation.`
    case 'channel_failed':
      return 'Invitation failed, try another channel to resend the invitation.'
    case 'channel_bounced':
      return 'Invitation bounced, try another channel to resend the invitation.'
  }
}

/**
 * Resolves blocked-send UI state for a channel panel.
 * Finished events hide Send on every channel; then `is_opt_out` (email only); otherwise prefers history latest status, then collection-item latest status.
 */
export function resolveInvitationChannelBlockedSendState(params: {
  actionTaken: InvitationCollectionActionTaken | null | undefined
  channel: InvitationChannel
  historyLatestStatus?: string | null
  channelLatestStatus?: string | null
  inviteeName?: string | null
  isOptOut?: boolean | null
  isEventFinished?: boolean | null
}): InvitationChannelBlockedSendState {
  if (params.isEventFinished) {
    return {
      reason: 'event_ended',
      showSendCta: false,
      message: invitationChannelBlockedSendMessage('event_ended', params.inviteeName)
    }
  }

  if (params.isOptOut && EMAIL_ONLY_BLOCKING_CHANNELS.has(params.channel)) {
    return {
      reason: 'opt_out',
      showSendCta: false,
      message: invitationChannelBlockedSendMessage('opt_out', params.inviteeName)
    }
  }

  const latestStatus = params.historyLatestStatus || params.channelLatestStatus || null
  const reason = getInvitationChannelBlockedSendReason(params.actionTaken, latestStatus, params.channel)

  return {
    reason,
    showSendCta: reason == null,
    message: invitationChannelBlockedSendMessage(reason, params.inviteeName)
  }
}

/**
 * Resolves blocked-send UI state for a collection item + channel, using the shared history only when it belongs to that item + channel.
 * @param item {InvitationCollectionItem} - Collection item from the API.
 * @param channel {InvitationChannel} - Channel to resolve.
 * @param history {InvitationChannelHistoryState | null} - Shared channel history state.
 * @param isEventFinished {boolean} - Whether the event has ended.
 */
export function resolveBlockedSendStateForItem(
  item: InvitationCollectionItem,
  channel: InvitationChannel,
  history: InvitationChannelHistoryState | null,
  isEventFinished: boolean
): InvitationChannelBlockedSendState {
  return resolveInvitationChannelBlockedSendState({
    actionTaken: item.action_taken,
    channel,
    historyLatestStatus: getActiveInvitationChannelHistory(history, item.id, channel)?.entries[0]?.status,
    channelLatestStatus: getInvitationChannelLatestStatus(item, channel),
    inviteeName: item.fullname,
    isOptOut: item.is_opt_out,
    isEventFinished
  })
}

/**
 * Returns true when the channel Send CTA should remain available for this action-taken status.
 * Registered, Declined, and Unreachable hide Send; Pending and empty still allow it.
 * @param actionTaken {InvitationCollectionActionTaken | null | undefined} - API action taken value.
 */
export function canSendInvitationForActionTaken(actionTaken: InvitationCollectionActionTaken | null | undefined): boolean {
  if (actionTaken == null) return true
  return !BLOCKED_SEND_ACTION_TAKEN.has(actionTaken)
}
